import React, { useState } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, FormControlLabel, Checkbox, Link, InputAdornment, IconButton, useMediaQuery, useTheme, Alert, LinearProgress } from '@mui/material';
import { Icon } from '@iconify/react';
interface LoginProps {
  onLogin: (username: string, password: string) => boolean;
}
const Login: React.FC<LoginProps> = ({
  onLogin
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    setLoading(true);
    // Simulate network request
    setTimeout(() => {
      const success = onLogin(username, password);
      if (!success) {
        setError('Invalid username or password');
        setLoading(false);
      }
    }, 800);
  };
  return <Box sx={{
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    position: 'relative'
  }} data-id="element-4609">
      {!isMobile && <Box sx={{
      flex: 1,
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 4,
      overflow: 'hidden',
      position: 'relative'
    }} data-id="element-4610">
          <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 60,
        mb: 3,
        transition: 'all 200ms ease'
      }} data-id="element-4611" />
          <Typography variant="h4" component="h1" fontWeight="700" gutterBottom color="#1F2937" sx={{
        mb: 2
      }} data-id="element-4612">
            Simplify Cross-Border Payments
          </Typography>
          <Typography variant="body1" sx={{
        maxWidth: 450,
        textAlign: 'center',
        color: '#6B7280',
        lineHeight: 1.6
      }} data-id="element-4613">
            Manage remittances, track transactions, and ensure compliance all
            from one powerful dashboard.
          </Typography>
          <Box component="img" src="https://img.freepik.com/free-vector/global-money-transfer-abstract-concept-illustration_335657-3893.jpg" alt="Global Payments Illustration" sx={{
        width: '70%',
        maxWidth: 400,
        mt: 4
      }} data-id="element-4614" />
        </Box>}
      <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 2,
      backgroundColor: '#0385FB',
      overflow: 'auto',
      position: 'relative'
    }} data-id="element-4615">
        <Card sx={{
        maxWidth: 400,
        width: '100%',
        py: 1.5,
        position: 'relative',
        zIndex: 2,
        overflow: 'visible',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
        borderRadius: '8px'
      }} elevation={0} data-id="element-4616">
          {loading && <LinearProgress sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8
        }} data-id="element-4617" />}
          <CardContent sx={{
          padding: {
            xs: 2.5,
            sm: 3.5
          },
          backgroundColor: '#FFFFFF'
        }} data-id="element-4618">
            <Box sx={{
            textAlign: 'center',
            mb: 3
          }} data-id="element-4619">
              {isMobile && <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
              height: 50,
              mb: 1.5
            }} data-id="element-4620" />}
              <Typography variant="h5" component="h1" fontWeight="700" color="#1F2937" data-id="element-4621">
                Welcome to OpenRemit Backoffice
              </Typography>
              <Typography variant="body2" color="#6B7280" mt={0.5} data-id="element-4622">
                Sign in to access your dashboard
              </Typography>
              <Box sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: '#F0F3F7',
              borderRadius: 2,
              border: '1px solid #E5E7EB'
            }} data-id="element-4623">
                <Typography variant="caption" color="#6B7280" display="block" fontWeight={600} sx={{
                mb: 0.5
              }} data-id="element-4624">
                  Demo Credentials
                </Typography>
                <Typography variant="caption" color="#6B7280" display="block" fontFamily="monospace" data-id="element-4625">
                  Maker: admin / test@1234
                </Typography>
                <Typography variant="caption" color="#6B7280" display="block" fontFamily="monospace" data-id="element-4626">
                  Checker: checker / test@1234
                </Typography>
              </Box>
            </Box>
            {error && <Alert severity="error" sx={{
            mb: 1.5,
            fontSize: '0.8rem',
            borderRadius: 2,
            animation: 'shake 0.5s ease-in-out',
            '@keyframes shake': {
              '0%, 100%': {
                transform: 'translateX(0)'
              },
              '10%, 30%, 50%, 70%, 90%': {
                transform: 'translateX(-5px)'
              },
              '20%, 40%, 60%, 80%': {
                transform: 'translateX(5px)'
              }
            }
          }} onClose={() => setError('')} data-id="element-4627">
                {error}
              </Alert>}
            <Box component="form" onSubmit={handleSubmit} data-id="element-4628">
              <TextField label="Username" variant="outlined" fullWidth margin="normal" value={username} onChange={e => setUsername(e.target.value)} required size="small" sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0052CC',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0052CC'
              }
            }} disabled={loading} data-id="element-4629" />
              <TextField label="Password" type={showPassword ? 'text' : 'password'} variant="outlined" fullWidth margin="normal" value={password} onChange={e => setPassword(e.target.value)} required size="small" InputProps={{
              endAdornment: <InputAdornment position="end" data-id="element-4631">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" disabled={loading} data-id="element-4632">
                        {showPassword ? <Icon icon="lucide:eye-off" width={18} height={18} data-id="element-4633" /> : <Icon icon="lucide:eye" width={18} height={18} data-id="element-4634" />}
                      </IconButton>
                    </InputAdornment>
            }} sx={{
              mb: 0.5,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0052CC',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0052CC'
              }
            }} disabled={loading} data-id="element-4630" />
              <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 0.5,
              mb: 2
            }} data-id="element-4635">
                <FormControlLabel control={<Checkbox size="small" sx={{
                '&.Mui-checked': {
                  color: '#0052CC'
                }
              }} disabled={loading} data-id="element-4637" />} label={<Typography variant="body2" data-id="element-4638">Remember me</Typography>} data-id="element-4636" />
                <Link href="#" underline="hover" sx={{
                fontSize: '0.8rem',
                color: '#0052CC',
                fontWeight: 500,
                transition: 'all 200ms ease',
                '&:hover': {
                  color: '#003DA8',
                  textDecoration: 'none'
                }
              }} data-id="element-4639">
                  Forgot password?
                </Link>
              </Box>
              <Button type="submit" variant="contained" fullWidth sx={{
              py: 1.2,
              fontSize: '0.9rem',
              position: 'relative',
              overflow: 'hidden'
            }} disabled={loading} data-id="element-4640">
                {loading ? 'Signing in...' : 'Sign In'}
                {loading && <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.2)'
              }} data-id="element-4641">
                    <Box sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: '3px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    to: {
                      transform: 'rotate(360deg)'
                    }
                  }
                }} data-id="element-4642" />
                  </Box>}
              </Button>
              <Box sx={{
              textAlign: 'center',
              mt: 2
            }} data-id="element-4643">
                <Link href="#" underline="hover" sx={{
                fontSize: '0.8rem',
                color: '#0052CC',
                fontWeight: 500,
                transition: 'all 200ms ease',
                '&:hover': {
                  color: '#003DA8',
                  textDecoration: 'none'
                }
              }} data-id="element-4644">
                  Use 2FA code
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Typography variant="body2" color="#FFFFFF" sx={{
        mt: 3,
        fontSize: '0.75rem',
        fontWeight: 500,
        position: 'relative',
        zIndex: 2
      }} data-id="element-4645">
          Powered by Paysys Labs © 2026
        </Typography>
      </Box>
    </Box>;
};
export default Login;